// A validated assistant proposal becomes a Studio action, never a file tool.
// The operator's global action policy decides whether Iterion denies, asks,
// or executes. Every path still checks the exact live tab + revision; an
// untitled buffer can only gain a path through host-owned Save As.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { CheckIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useLocation } from "wouter";

import * as api from "@/api/client";
import { Button } from "@/components/ui/Button";
import DocumentSaveAsDialog from "@/components/DocumentSaveAs/DocumentSaveAsDialog";
import { useDocumentSaveAs } from "@/components/DocumentSaveAs/useDocumentSaveAs";
import { useEditorProposal } from "@/hooks/useEditorProposal";
import {
  decideAssistantAction,
  readAssistantActionPolicy,
  useAssistantActionPolicy,
} from "@/lib/chatDock/assistantActions";
import {
  isEditorSessionActive,
  resolveEditorSession,
} from "@/lib/chatDock/editorSession";
import { resolveProposalTarget } from "@/lib/chatDock/proposalTarget";
import { useRecentsStore } from "@/store/recents";
import { useServerInfoStore } from "@/store/serverInfo";
import { useTabsStore } from "@/store/tabs";
import { useUIStore } from "@/store/ui";
import { isSharedBundleFilePath } from "@/lib/sharedBundle";
import { applyParsedSource, salvageRefusal } from "@/lib/salvage";

type ActionState = "idle" | "applying" | "applied" | "saving" | "saved" | "error";

export default function EditorChangeOffer({
  runId,
  revision,
}: {
  runId: string | null;
  revision: number;
}) {
  const proposal = useEditorProposal(runId, revision);
  const [route, setLocation] = useLocation();
  const activeTabId = useTabsStore((state) => state.activeEditorTabId);
  const isCloud = useServerInfoStore((state) => state.info?.mode === "cloud");
  const addToast = useUIStore((state) => state.addToast);
  const pushRecent = useRecentsStore((state) => state.pushRecent);
  const saveAs = useDocumentSaveAs();
  const applyPolicy = useAssistantActionPolicy("editor.apply");
  const savePolicy = useAssistantActionPolicy("editor.save");
  const [appliedRevision, setAppliedRevision] = useState<number | null>(null);
  const [action, setAction] = useState<ActionState>("idle");
  const [error, setError] = useState<string | null>(null);
  const autoStarted = useRef<string | null>(null);
  const routeRef = useRef(route);
  useEffect(() => {
    routeRef.current = route;
  }, [route]);

  const sessionId = proposal.sessionId;
  const resolved = sessionId ? resolveEditorSession(sessionId) : null;
  const targetStore = resolved?.store;
  const subscribeRevision = useCallback(
    (notify: () => void) => targetStore?.subscribe(notify) ?? (() => {}),
    [targetStore],
  );
  const readRevision = useCallback(
    () => targetStore?.getState()._generation ?? null,
    [targetStore],
  );
  const storeRevision = useSyncExternalStore(
    subscribeRevision,
    readRevision,
    () => null,
  );

  const onEditor = route.startsWith("/editor");
  const stillActive = !!sessionId && isEditorSessionActive(sessionId);
  const expectedRevision = appliedRevision ?? proposal.revision;
  const revisionMatches =
    expectedRevision !== null && storeRevision === expectedRevision;
  const targetStale = !stillActive || !revisionMatches;
  // A live editor tab survives navigation by design. Keep the proposal, but
  // never let an automatic or confirmed action mutate an invisible canvas.
  const path = resolved?.store.getState().currentFilePath ?? null;
  const readOnly =
    isSharedBundleFilePath(path) ||
    (!!path && isCloud && api.parseBotSourceEditorPath(path) === null);
  // A bot in several files: the editor holds the merged program with each
  // declaration's file, and a proposal is ONE file's text (#1647) — applied
  // through the unit's re-parse, to the file the Source view's picker names.
  const readUnit = useCallback(
    () => targetStore?.getState().unit ?? null,
    [targetStore],
  );
  const unit = useSyncExternalStore(subscribeRevision, readUnit, () => null);
  const readSourceFile = useCallback(
    () => targetStore?.getState().sourceFile ?? null,
    [targetStore],
  );
  const pickedFile = useSyncExternalStore(subscribeRevision, readSourceFile, () => null);
  // A file the author asked for is still opening in that tab: that request
  // wins, and an apply landing first would get its answer refused for edits
  // nobody made. Available again once it ends — on the document it left.
  const readPending = useCallback(
    () => targetStore?.getState()._pendingIntent != null,
    [targetStore],
  );
  const replacementPending = useSyncExternalStore(subscribeRevision, readPending, () => false);
  // The per-file target: the picker's selection, with the model's
  // editor_file as a hint that can only refuse, never redirect (#1647).
  const proposalTarget = unit ? resolveProposalTarget(unit, pickedFile, proposal.file) : null;
  const unitBlocked = proposalTarget !== null && "reason" in proposalTarget;
  const unavailable = targetStale || !onEditor || readOnly || unitBlocked || replacementPending;
  const hasDraft = !!proposal.source;
  // Intent is model-reported but never grants authority: it can only select
  // the operator's preconfigured branch. Unknown/legacy intent is non-explicit
  // and therefore still asks under the "explicit" policy.
  const applyExplicit = proposal.applyIntent === "explicit";
  const applyDecision = decideAssistantAction(applyPolicy, applyExplicit);
  const saveExplicit = proposal.saveIntent === "explicit";
  const saveRequested = proposal.saveIntent !== "none";
  const saveOnly = !hasDraft && saveRequested;
  const saveDecision = decideAssistantAction(savePolicy, saveExplicit);

  const saveBoundFile = useCallback(
    async (proposalSession: string, generation: number): Promise<void> => {
      const current = resolveEditorSession(proposalSession);
      if (
        !current ||
        !isEditorSessionActive(proposalSession) ||
        !routeRef.current.startsWith("/editor") ||
        current.store.getState()._generation !== generation
      ) {
        throw new Error(
          "The editor changed before it could be saved. The proposed change remains in the buffer.",
        );
      }
      const state = current.store.getState();
      const currentPath = state.currentFilePath;
      if (!currentPath) {
        throw new Error(
          "This buffer has no file yet. Use Save As in the editor to choose its location.",
        );
      }
      if (isSharedBundleFilePath(currentPath)) {
        throw new Error(
          "This workflow comes from a locked shared bundle. Edit the source bundle and sync the dependency before saving.",
        );
      }
      if (isCloud && api.parseBotSourceEditorPath(currentPath) === null) {
        throw new Error(
          "This catalog bot is read-only. Duplicate it before saving changes.",
        );
      }
      if (!state.document) throw new Error("The editor document is unavailable.");
      const refusal = salvageRefusal(state);
      if (refusal) throw new Error(refusal);

      setAction("saving");
      const savedGeneration = state._generation;
      // The digest rule: the save presents the digest this tab's open
      // issued — a concurrent edit is a 409, never an overwrite. A bot in
      // several files presents its unit's REVISION instead (its files carry
      // their own digests, judged with it): without it the save lands on
      // saveUnit's "names no revision" branch (#1647), whose message tells
      // the author to reopen a file that is already open.
      const digest = targetStore?.getState().openedDigest ?? undefined;
      const result = state.unit
        ? await api.saveFile(currentPath, state.document, {
            revision: state.unit.revision,
            unitFiles: state.unit.files,
          })
        : await api.saveFile(currentPath, state.document, digest ? { digest } : undefined);
      const afterSave = resolveEditorSession(proposalSession);
      if (
        afterSave &&
        afterSave.store.getState()._generation === savedGeneration
      ) {
        const savedState = afterSave.store.getState();
        savedState.setCurrentSource(result.source);
        // The answer carries what the next save of this file presents (the
        // digest rule): held stale, the offer's second save is a 409
        // against its own first.
        savedState.setOpenedDigest(result.digest ?? null);
        // A unit's claim is its revision, which this write advanced: the
        // next save presents the one the answer carries.
        if (savedState.unit) {
          savedState.setUnit({ ...savedState.unit, revision: result.revision ?? savedState.unit.revision });
        }
        savedState.markSaved();
        setAction("saved");
      } else {
        setAction("applied");
        addToast("Saved, but newer editor changes remain unsaved", "warning");
      }
      pushRecent(currentPath);
    },
    [addToast, isCloud, pushRecent],
  );

  const saveEditorAtGeneration = useCallback(
    async (proposalSession: string, generation: number): Promise<void> => {
      const current = resolveEditorSession(proposalSession);
      if (
        !current ||
        !isEditorSessionActive(proposalSession) ||
        !routeRef.current.startsWith("/editor") ||
        current.store.getState()._generation !== generation
      ) {
        throw new Error(
          "The editor changed before it could be saved. Ask again from the current buffer.",
        );
      }

      const state = current.store.getState();
      if (state.currentFilePath) {
        await saveBoundFile(proposalSession, generation);
        return;
      }
      if (isCloud) {
        throw new Error(
          "Save As isn't available in cloud. Create a bot from the Bots page first.",
        );
      }

      const targetStore = current.store;
      const opened = saveAs.requestSaveAs({
        store: targetStore,
        expectedGeneration: generation,
        isTargetCurrent: () => {
          const live = resolveEditorSession(proposalSession);
          return (
            !!live &&
            live.store === targetStore &&
            isEditorSessionActive(proposalSession) &&
            routeRef.current.startsWith("/editor")
          );
        },
        validate: () =>
          decideAssistantAction(
            readAssistantActionPolicy("editor.save"),
            saveExplicit,
          ) === "deny"
            ? "Saving assistant changes is disabled in Settings → Assistant."
            : null,
        onSaved: (result) => {
          setAction(result.clean ? "saved" : "applied");
        },
      });
      if (!opened) {
        throw new Error("Could not open Save As for the current editor buffer.");
      }
      // The validated source is now in memory. Persistence remains pending
      // until the operator confirms a destination in the shared dialog.
      setAction("applied");
    },
    [isCloud, saveAs, saveBoundFile, saveExplicit],
  );

  const applyProposal = useCallback(
    async (saveAfter: boolean) => {
      if (!proposal.source || !proposal.sessionId || proposal.revision === null) return;
      if (applyDecision === "deny") {
        setError("Applying assistant changes is disabled in Settings → Assistant.");
        setAction("error");
        return;
      }
      if (saveAfter && saveDecision === "deny") {
        setError("Saving assistant changes is disabled in Settings → Assistant.");
        setAction("error");
        return;
      }
      const current = resolveEditorSession(proposal.sessionId);
      if (
        !current ||
        !routeRef.current.startsWith("/editor") ||
        !isEditorSessionActive(proposal.sessionId) ||
        current.store.getState()._generation !== proposal.revision
      ) {
        setError(
          "The editor tab, page, or document revision changed. Return to the captured buffer and ask again if needed.",
        );
        setAction("error");
        return;
      }
      if (isSharedBundleFilePath(current.store.getState().currentFilePath)) {
        setError(
          "This workflow comes from a locked shared bundle. Edit its source bundle instead.",
        );
        setAction("error");
        return;
      }
      if (saveAfter) {
        const currentPath = current.store.getState().currentFilePath;
        if (
          currentPath &&
          isCloud &&
          api.parseBotSourceEditorPath(currentPath) === null
        ) {
          setError("This catalog bot is read-only. Duplicate it before saving changes.");
          setAction("error");
          return;
        }
      }

      setAction("applying");
      setError(null);
      try {
        // A bot in several files: the proposal is ONE file's text, re-parsed
        // through the unit — the server rebuilds the merged document from
        // the files as stored plus this overlay — and never through
        // parseSource, which would read a fragment as a whole program. The
        // file is the Source view picker's selection; the model's
        // editor_file hint can only refuse a mismatch, never pick (#1647).
        const unitNow = current.store.getState().unit;
        let targetRel: string | null = null;
        let parsed:
          | Awaited<ReturnType<typeof api.parseSource>>
          | Awaited<ReturnType<typeof api.parseUnitFile>>;
        if (unitNow) {
          const decision = resolveProposalTarget(
            unitNow,
            current.store.getState().sourceFile,
            proposal.file,
          );
          if ("reason" in decision) throw new Error(decision.reason);
          targetRel = decision.rel;
          const unitPath = current.store.getState().currentFilePath;
          if (!unitPath) {
            throw new Error("The editor's file is unknown; reopen the bot.");
          }
          parsed = await api.parseUnitFile(unitPath, decision.rel, proposal.source);
        } else {
          parsed = await api.parseSource(proposal.source);
        }
        // For a unit the path travels, so the merged document compiles with
        // the bundle's prompts in scope, the way a launch compiles it.
        const validated = await api.validate(
          parsed.document,
          undefined,
          unitNow ? current.store.getState().currentFilePath : undefined,
        );
        const diagnostics = [
          ...(parsed.diagnostics ?? []),
          ...(validated.diagnostics ?? []),
        ];
        if (diagnostics.length > 0) {
          throw new Error(
            `The proposal has ${diagnostics.length} validation error${diagnostics.length === 1 ? "" : "s"}.`,
          );
        }

        // Parsing/validation is asynchronous. Re-check afterwards so a late
        // result cannot clobber an edit the operator made meanwhile.
        const afterValidation = resolveEditorSession(proposal.sessionId);
        // A file the author asked for began opening in that tab meanwhile:
        // the proposal waits for it, as every background write does, and is
        // looked at again once it ends — by the automatic run, which starts
        // again when the tab is available, or by the Apply button.
        if (
          afterValidation &&
          afterValidation.store.getState()._pendingIntent != null &&
          afterValidation.store.getState()._generation === proposal.revision
        ) {
          autoStarted.current = null;
          setAction("idle");
          return;
        }
        if (
          !afterValidation ||
          !routeRef.current.startsWith("/editor") ||
          !isEditorSessionActive(proposal.sessionId) ||
          afterValidation.store.getState()._generation !== proposal.revision
        ) {
          throw new Error(
            "The editor changed while the proposal was being validated. Nothing was applied.",
          );
        }
        // The picker is no part of the generation: it can move while the
        // round trip is out, and applying file A's text under file B's name
        // is exactly what the target resolution exists to refuse.
        if (unitNow) {
          const again = resolveProposalTarget(
            afterValidation.store.getState().unit ?? unitNow,
            afterValidation.store.getState().sourceFile,
            proposal.file,
          );
          if ("reason" in again || again.rel !== targetRel) {
            throw new Error(
              "The Source view's picker moved while the proposal was being validated. Nothing was applied.",
            );
          }
        }
        const state = afterValidation.store.getState();
        // Document and verdict together: a proposal that parses whole ends
        // the salvage of a file that did not parse, so a buffer opened on an
        // unreadable file becomes writable again — that is how Copi repairs
        // one. A salvage the server's READER made (a read budget) is not
        // ended: Copi never saw that file, and the proposal is not it.
        applyParsedSource(parsed, state);
        const after = afterValidation.store.getState();
        if (unitNow) {
          // The verdict on the files as they are STORED rides the answer —
          // the per-file parse, the load, the unread fragments — and is
          // installed as the Source view's own apply installs it, so the
          // launch gate never stands on a reading the server has just
          // contradicted. The root and the revision are NOT taken: an
          // overlay wrote nothing, so it presents neither.
          const parsedUnit = "unit" in parsed ? parsed.unit : undefined;
          const appliedUnit = after.unit;
          if (parsedUnit && appliedUnit) {
            after.setUnit({
              ...appliedUnit,
              files: parsedUnit.files,
              unread: parsedUnit.unread,
              load_errors: parsedUnit.load_errors,
              load_error: parsedUnit.load_error,
            });
          }
          // currentSource is what a resume sends inline AS the program:
          // only the main's text may land there — a fragment declares no
          // workflow.
          if (targetRel === appliedUnit?.main) {
            after.setCurrentSource(proposal.source);
          }
        } else {
          after.setCurrentSource(proposal.source);
        }
        after.setDiagnostics(
          validated.diagnostics ?? [],
          validated.warnings ?? [],
          validated.issues ?? ("issues" in parsed ? parsed.issues : undefined) ?? [],
        );
        const generation = afterValidation.store.getState()._generation;
        setAppliedRevision(generation);

        if (saveAfter) {
          await saveEditorAtGeneration(proposal.sessionId, generation);
        } else {
          setAction("applied");
          addToast(
            "Assistant change applied to the editor — not saved yet",
            "success",
          );
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Could not apply the proposal";
        setError(message);
        setAction("error");
      }
    },
    [
      proposal,
      applyDecision,
      saveDecision,
      isCloud,
      saveEditorAtGeneration,
      addToast,
    ],
  );

  const save = useCallback(async () => {
    const generation = appliedRevision ?? proposal.revision;
    if (!proposal.sessionId || generation === null) return;
    if (saveDecision === "deny") {
      setError("Saving assistant changes is disabled in Settings → Assistant.");
      setAction("error");
      return;
    }
    setError(null);
    try {
      await saveEditorAtGeneration(proposal.sessionId, generation);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
      setAction("error");
    }
  }, [
    proposal.sessionId,
    proposal.revision,
    appliedRevision,
    saveDecision,
    saveEditorAtGeneration,
  ]);

  // Auto execution is a policy decision, not a model shortcut. The ref makes
  // React StrictMode/remount churn idempotent within this mounted offer; the
  // revision guard is the durable second line of defence.
  useEffect(() => {
    if (
      !proposal.sessionId ||
      proposal.revision === null ||
      unavailable ||
      action !== "idle"
    ) {
      return;
    }
    const key = `${proposal.sessionId}:${proposal.revision}`;
    if (autoStarted.current === key) return;
    if (hasDraft && applyDecision === "auto") {
      autoStarted.current = key;
      const autoSave = saveRequested && saveDecision === "auto";
      void applyProposal(autoSave);
    } else if (saveOnly && saveDecision === "auto") {
      autoStarted.current = key;
      void save();
    }
  }, [
    proposal,
    unavailable,
    action,
    hasDraft,
    saveOnly,
    applyDecision,
    saveRequested,
    saveDecision,
    applyProposal,
    save,
  ]);

  if (
    (!hasDraft && !saveOnly) ||
    !proposal.sessionId ||
    proposal.revision === null
  ) {
    return null;
  }

  const hasApplied = appliedRevision !== null;
  const needsReturn = !onEditor || activeTabId !== resolved?.tabId;
  const canApply =
    !unavailable && applyDecision !== "deny" && action !== "applying";
  const canSave =
    (hasApplied || saveOnly) &&
    action !== "saved" &&
    !unavailable &&
    (!isCloud || !!path) &&
    saveDecision !== "deny";
  const returnToEditor = () => {
    if (!resolved) return;
    useTabsStore.getState().setActive(resolved.tabId);
    setLocation(
      path ? `/editor?file=${encodeURIComponent(path)}` : "/editor",
    );
  };

  let detail = saveOnly
    ? path
      ? "Save the current host-owned editor buffer to its existing file."
      : "Choose a destination for the current host-owned editor buffer."
    : proposalTarget && "rel" in proposalTarget
      ? `Apply the proposed ${proposalTarget.rel} to the live buffer; you can undo or save afterwards.`
      : "Apply changes only the live buffer; you can undo or save afterwards.";
  if (action === "saved") detail = path ?? "Saved";
  else if (needsReturn) detail = "Return to the captured editor tab to review or run this action.";
  else if (proposalTarget && "reason" in proposalTarget) detail = proposalTarget.reason;
  else if (readOnly) detail = "This locked shared bundle is read-only in the consumer project.";
  else if (!revisionMatches) detail = "The document changed since this proposal was created.";
  else if (saveOnly && saveDecision === "deny") detail = "Saving assistant changes is disabled in Settings → Assistant.";
  else if (hasDraft && applyDecision === "deny") detail = "Applying assistant changes is disabled in Settings → Assistant.";
  else if (replacementPending) detail = "Waiting for the file opening in that editor tab.";
  else if (action === "applying") detail = "Validating and applying the proposed bot…";
  else if (action === "saving") detail = "Saving the validated buffer to its existing file…";
  else if (action === "applied") {
    detail = saveOnly
      ? "Nothing has been written to disk. Confirm a destination to save this buffer."
      : path
        ? "Applied to the live buffer. Nothing has been written to disk."
        : "Applied to the live buffer. Choose a location to write it to disk.";
  } else if (applyDecision === "auto") {
    detail = "Authorized by your Assistant settings. Validation will run before application.";
  }

  return (
    <>
    <div className="mt-3 rounded-md border border-border-subtle bg-surface-2 p-2.5">
      <div className="flex items-start gap-2">
        {action === "saved" ? (
          <CheckIcon className="mt-0.5 h-4 w-4 text-success-fg" aria-hidden="true" />
        ) : (
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 text-accent-text" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-label font-medium">
            {action === "saved"
              ? "Editor change saved"
              : saveOnly
                ? "Save editor buffer"
                : "Proposed editor change"}
          </p>
          <p className="mt-0.5 text-caption text-fg-muted">{detail}</p>
          {error && <p className="mt-1 text-caption text-danger-fg">{error}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {needsReturn && resolved && action !== "saved" && (
              <Button variant="secondary" size="sm" onClick={returnToEditor}>
                Return to the bot
              </Button>
            )}
            {hasDraft &&
              !hasApplied &&
              action !== "saved" &&
              applyDecision === "confirm" && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canApply}
                    onClick={() => void applyProposal(false)}
                  >
                    {action === "applying" ? "Validating…" : "Apply to editor"}
                  </Button>
                  {saveDecision !== "deny" && (!isCloud || !!path) && (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!canApply}
                      onClick={() => void applyProposal(true)}
                    >
                      Apply and save
                    </Button>
                  )}
                </>
              )}
            {(hasApplied || saveOnly) &&
              action !== "saved" &&
              saveDecision !== "deny" && (
              <Button
                variant="primary"
                size="sm"
                disabled={!canSave || action === "saving"}
                onClick={() => void save()}
              >
                {action === "saving"
                  ? "Saving…"
                  : path
                    ? "Save current file"
                    : "Choose location and save"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    <DocumentSaveAsDialog controller={saveAs} />
    </>
  );
}
